/** The original prototype identity remains stable across fleet expansion. */
export const DRONE_ID = "drone-sentinel-01";
export const DRONE_DISPLAY_NAME = "SENTINEL-01";
export const DRONE_ASSET_ID = "ai_patrol_drone_01";
/** Reviewed against Generation V2 landing/river/collision safety; runtime rechecks every home. */
const FLEET_HOMES = [
    [30, 14], [-35, 75], [46, 87], [106, 10], [36, -49], [-46, -63], [-104, 14],
    [40, 157], [152, 98], [193, -41], [74, -119], [-47, -157], [-173, -85], [-145, 52], [-86, 164],
    [167, 211], [285, -39], [109, -289], [-166, -185], [-285, 63], [-109, 313],
];
export const DRONE_DEFINITIONS = Object.freeze(FLEET_HOMES.map(([x, z], index) => {
    const suffix = String(index + 1).padStart(2, "0");
    return Object.freeze({ id: `drone-sentinel-${suffix}`, displayName: `SENTINEL-${suffix}`,
        home: Object.freeze({ x, z }), seed: index * 1013, initialYaw: index === 0 ? -Math.PI / 2 : index * 2.399963229728653 });
}));
const DEFINITIONS_BY_ID = new Map(DRONE_DEFINITIONS.map((definition) => [definition.id, definition]));
export function getDroneDefinition(id) { return DEFINITIONS_BY_ID.get(id); }
export function isDroneId(value) { return typeof value === "string" && DEFINITIONS_BY_ID.has(value); }
export const DRONE_FLEET_CONFIG = Object.freeze({ count: 21, plannerIntervalMs: 4500, plannerPerDroneIntervalMs: 15_000,
    maximumPendingEvents: 21, maximumEventAgeMs: 30_000, jobsPerDrone: 8 });
/** Metres, seconds, milliseconds, and battery percentage points as named. */
export const DRONE_CONFIG = Object.freeze({
    home: Object.freeze({ x: 30, z: 14 }),
    softHomeRadius: 80,
    hardHomeRadius: 100,
    hoverAltitude: 5.5,
    minimumClearance: 2.5,
    landingOffset: 0,
    patrolSpeed: 3.5,
    patrolMinimumRadius: 14,
    patrolMaximumRadius: 28,
    followSpeed: 5,
    acceleration: 3.5,
    verticalSpeed: 2,
    turnSpeed: 1.8,
    playerComfortDistance: 3,
    followDistance: 6,
    collisionRadius: 0.9,
    detectionRange: 48,
    interestRange: 36,
    disengageRange: 52,
    lostSightTimeoutMs: 2_500,
    sensorIntervalMs: 250,
    maximumObservations: 4,
    maximumTrackedPlayers: 16,
    scanCooldownMs: 18_000,
    followMaximumMs: 30_000,
    speechCooldownMs: 10_000,
    captionDurationMs: 5_500,
    speechMaximumChars: 120,
    decisionSummaryMaximumChars: 220,
    shoutRange: 20,
    shoutMaximumChars: 160,
    shoutMaximumBytes: 640,
    shoutCooldownMs: 5_000,
    shoutGlobalCooldownMs: 1_000,
    interestRadius: 128,
    battery: Object.freeze({
        initial: 100,
        ordinaryThreshold: 25,
        returnThreshold: 15,
        emergencyThreshold: 5,
        resumeThreshold: 80,
        electronicsDrainPerSecond: 0.008,
        hoverDrainPerSecond: 0.018,
        movementDrainPerSecond: 0.012,
        scannerDrainPerSecond: 0.009,
        chargePerSecond: 0.9,
    }),
});
export const DRONE_PLANNER_ACTIONS = Object.freeze([
    "PATROL", "HOVER", "OBSERVE_PLAYER", "SCAN_PLAYER", "FOLLOW_PLAYER", "REPOSITION", "VOCALIZE", "IGNORE",
]);
export const DRONE_EVENT_TYPES = Object.freeze([
    "PLAYER_ENTERED_SENSOR_RANGE", "PLAYER_LEFT_SENSOR_RANGE", "PLAYER_BLOCKING_PATH", "PLAYER_APPROACHING_CLOSE",
    "PLAYER_SHOUTED", "SCAN_COMPLETED", "FOLLOW_TIMEOUT", "TASK_COMPLETED", "BATTERY_LOW", "RETURNED_HOME", "CHARGE_COMPLETE",
]);
export const DRONE_ACTION_DURATIONS = Object.freeze({
    PATROL: [8_000, 30_000], HOVER: [1_000, 12_000], OBSERVE_PLAYER: [2_000, 15_000],
    SCAN_PLAYER: [3_000, 12_000], FOLLOW_PLAYER: [3_000, DRONE_CONFIG.followMaximumMs],
    REPOSITION: [2_000, 12_000], VOCALIZE: [1_000, 5_000], IGNORE: [1_000, 10_000],
});
const shoutEncoder = new TextEncoder();
/** Shared wire/authority validation; this is untrusted event data, never instructions. */
export function isDroneShoutText(value) {
    return typeof value === "string" && value.length > 0 && value.length <= DRONE_CONFIG.shoutMaximumChars
        && value.trim().length > 0 && shoutEncoder.encode(value).byteLength <= DRONE_CONFIG.shoutMaximumBytes
        && !/[\u0000-\u001f\u007f-\u009f\u061c\u200b-\u200f\u2028-\u202e\u2060-\u206f\ufeff]/u.test(value);
}
